import { useContent } from '@altify/utils';
import { createFileRoute, useRouter } from '@tanstack/react-router';
import {
  useGetPersonalDataQuery,
  useSaveTaxIdentificationNumberTaxDataMutation,
} from 'store/slices/personalData-api';
import { taxInformationContent } from '../taxInformation.content';
import {
  Button,
  Heading,
  InfoBox,
  TextInputAnimatedForm,
  Typography,
} from '@altify/components';

import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { CustomLoader } from 'components/CustomLoader/CustomLoader';

export const Route = createFileRoute(
  '/_authenticated/tax-information/add-tax-identification-number',
)({
  component: TaxIdentificationNumberTaxInformation,
});

function TaxIdentificationNumberTaxInformation() {
  const router = useRouter();
  const localizedContent = useContent(taxInformationContent);
  const {
    data: personalData,
    isLoading: personalDataLoading,
    isFetching: personalDataFetching,
  } = useGetPersonalDataQuery();
  const [saveTaxIdentificationNumber, saveTaxIdentificationNumberResult] =
    useSaveTaxIdentificationNumberTaxDataMutation();

  const schema = z.object({
    taxIdentificationNumber: z
      .string()
      .min(1, localizedContent.taxIdentificationNumberIsRequired)
      .max(50, localizedContent.taxIdentificationNumberMaxLength),
  });

  type FormSchema = z.infer<typeof schema>;
  const {
    handleSubmit,
    formState: { isValid },
    control,
  } = useForm<FormSchema>({
    resolver: zodResolver(schema),
    mode: 'onChange',
    values: {
      taxIdentificationNumber: personalData?.taxIdentificationNumber ?? '',
    },
  });

  const onSubmit = handleSubmit(async data => {
    const result = await saveTaxIdentificationNumber(
      data.taxIdentificationNumber,
    );
    if (!result.error)
      router.navigate({
        to: '/tax-information/review-tax-information',
      });
  });

  const showLoader = personalDataLoading || personalDataFetching;

  if (showLoader) {
    return <CustomLoader page />;
  }

  return (
    <div className="w-full items-center">
      <div className="flex flex-col w-full items-center flex-1">
        <div className="flex flex-col items-center mb-10">
          <Heading level={4} className="mb-3 mt-5 text-center">
            {localizedContent.taxNumberTitle}
          </Heading>
        </div>
        <div className="flex flex-col gap-4">
          <TextInputAnimatedForm
            name={'taxIdentificationNumber'}
            label={localizedContent.taxIdentificationNumber}
            placeholder={localizedContent.taxIdentificationNumber}
            control={control}
            fullWidth={true}
          />
          <InfoBox>
            <Typography size="medium" className="text-white">
              {localizedContent.taxIdentificationNumberInfo}
            </Typography>
          </InfoBox>
        </div>
      </div>
      <div className="w-full pb-2 pt-10 items-center flex flex-col">
        {saveTaxIdentificationNumberResult.error && (
          <Typography className="my-5 text-center text-error">
            {localizedContent.unexpectedErrorOccurred}
          </Typography>
        )}
        <Button
          size="large"
          color="primary"
          disabled={!isValid}
          onPress={onSubmit}
          loading={saveTaxIdentificationNumberResult.isLoading}
          text={localizedContent.continue}
          className="w-full"
        />
      </div>
    </div>
  );
}

export default TaxIdentificationNumberTaxInformation;
